import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  chakra,
  useColorModeValue,
  Spinner,
  Center,
  Flex,
  Button,
  Text,
  HStack,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { GetInventoryData } from "../../InventoryServices/InventoryAPI/GetInventoryData";

// Format date
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const ProductTable = ({ filters = {}, searchTerm = '' }) => {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // Theme values
  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");

  const handleRowClick = (item) => {
    history.push({
      pathname: `/home/ap/product-inventory/tabs/${item.id}`,
      state: { title: item.title, category: item.category, title_block: item.title_block },
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Combine filters with search term
        const combinedFilters = { ...filters };
        
        // Add search term to filters if it exists
        if (searchTerm.trim()) {
          combinedFilters.search = searchTerm;
        }

        const res = await GetInventoryData({ 
          limit, 
          page, 
          filters: combinedFilters 
        });
        
        if (isMounted) {
          const data = Array.isArray(res.data.data)
            ? res.data.data
            : [res.data.data];

          setItems(data);
          setTotal(res.data.total || 0);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch products:", error);
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, [page, limit, filters, searchTerm]);

  // Reset to first page when filters or search changes
  useEffect(() => {
    setPage(1);
  }, [filters, searchTerm]);

  const capitalizeFirst = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box p={6} bg={tableBg} borderRadius="md" border="1px solid" borderColor="gray.200" shadow="sm">
      <TableContainer
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        maxH="560px"
        overflowY="auto"
      >
        <Table variant="simple" size="md">
          <Thead bg={headerBg}>
            <Tr>
              <Th color={headerColor} fontSize="sm">Product</Th>
              <Th color={headerColor} fontSize="sm">Code</Th>
              <Th color={headerColor} fontSize="sm">Category</Th>
              <Th color={headerColor} fontSize="sm">Created Date</Th>
              <Th color={headerColor} fontSize="sm">Updated Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={5}>
                  <Center py={10}>
                    <Spinner size="lg" color="blue.500" />
                  </Center>
                </Td>
              </Tr>
            ) : items.length === 0 ? (
              <Tr>
                <Td colSpan={5}>
                  <Center py={10}>
                    <Text color="gray.500">No products found</Text>
                  </Center>
                </Td>
              </Tr>
            ) : (
              <>
                {items.map((item) => (
                  <Tr
                    key={item.id}
                    _hover={{
                      bg: rowHoverBg,
                      transform: "scale(1.01)",
                      boxShadow: "md",
                      transition: "all 0.2s",
                      cursor: "pointer",
                    }}
                    onClick={() => handleRowClick(item)}
                  >
                    <Td fontWeight="600">{capitalizeFirst(item.title)}</Td>
                    <Td>{item.productCode || '-'}</Td>
                    <Td>{capitalizeFirst(item.category)}</Td>
                    <Td>
                      <chakra.span fontSize="sm" color="gray.600">
                        {formatDateTime(item.created_at)}
                      </chakra.span>
                    </Td>
                    <Td>
                      <chakra.span fontSize="sm" color="gray.600">
                        {formatDateTime(item.updated_at)}
                      </chakra.span>
                    </Td>
                  </Tr>
                ))}

                {/* Fill empty rows to maintain table height */}
                {items.length < limit &&
                  Array.from({ length: limit - items.length }).map((_, i) => (
                    <Tr key={`empty-${i}`} h="40px">
                      <Td>&nbsp;</Td>
                      <Td></Td>
                      <Td></Td>
                      <Td></Td>
                      <Td></Td>
                    </Tr>
                  ))}
              </>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={4}>
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              isDisabled={page === 1}
            >
              Prev
            </Button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={page === pageNum ? "solid" : "outline"}
                  colorScheme={page === pageNum ? "blue" : "gray"}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <Button
              size="sm"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              isDisabled={page === totalPages}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Results Summary */}
      <Flex justify="center" mt={2}>
        <Text fontSize="sm" color="gray.600">
          Showing {items.length} of {total} products
        </Text>
      </Flex>
    </Box>
  );
};

export default ProductTable;